import { v4 as uuid } from 'uuid';
import { FloorplanRevision, Point, Obstacle, Segment, Rectangle } from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { logger } from '@common/logging/logger';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { getName } from '@/modules/floorplanDesign/helpers/name';

import { AtLeast2, AtLeast1 } from './minItems';
import { RectangleUtils } from './RectangleUtils';

export const getObstacleInitializationHeight = (
  obstacleAppearance: FloorplanRevision['design']['obstacles'][number]['appearance'],
): number | undefined => {
  const { getPreference } = useFloorplanDesignStore.getState();

  switch (obstacleAppearance) {
    case 'BOX':
      return getPreference('box.height');
    case 'WALL':
      return getPreference('wall.height');
    case 'PILLAR':
      return getPreference('pillar.height');
    case 'WARNING_ZONE':
      return undefined;
    default:
      assertNever(obstacleAppearance);
  }
};

export const buildWall = (
  points: AtLeast2<Point>,
  height: number,
  thickness: number,
  wallMaterial: Obstacle['wallMaterial'],
): FloorplanRevision['design']['obstacles'][number] | undefined => {
  const segments: Segment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push({
      id: uuid(),
      leftSideWidth: thickness / 2,
      rightSideWidth: thickness / 2,
      start: points[i],
      end: points[i + 1],
      height,
    });
  }

  if (segments.length < 1) {
    logger.error('Expected at least 1 segment in order to create a wall');
    return;
  }

  const name = getName('wall');

  return {
    id: uuid(),
    name,
    appearance: 'WALL',
    segments: segments as AtLeast1<Segment>,
    wallMaterial,
  };
};

export const buildGenericObstacle = (
  appearance: FloorplanRevision['design']['obstacles'][number]['appearance'],
  rectangle: Rectangle,
  height: number | undefined,
): FloorplanRevision['design']['obstacles'][number] => {
  const segment = {
    ...RectangleUtils.toPartialSegment(rectangle),
    id: uuid(),
    height,
  };

  return {
    id: uuid(),
    name: getName('obstacle'),
    appearance,
    segments: [segment],
  };
};
